interface CachedProfile {
  data: any;
  timestamp: number;
  expiresAt: number;
}

class ProfileCacheService {
  private cache: Map<string, CachedProfile> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_CACHE_SIZE = 500;
  private cleanupIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly CLEANUP_INTERVAL = 60 * 1000; // Every minute

  /**
   * Get a profile from cache
   */
  get(userId: string): any | null {
    const cached = this.cache.get(userId);
    
    if (!cached) return null;
    
    // Check if expired
    if (Date.now() > cached.expiresAt) {
      this.cache.delete(userId);
      return null;
    }
    
    return cached.data;
  }

  /**
   * Store a profile in cache
   */
  set(userId: string, profile: any): void {
    // Implement LRU eviction if cache is too large
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      const oldestKey = this.findOldestEntry();
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    const now = Date.now();
    this.cache.set(userId, {
      data: profile,
      timestamp: now,
      expiresAt: now + this.CACHE_DURATION
    });
  }

  /**
   * Store multiple profiles
   */
  setMany(profiles: Record<string, any>): void {
    Object.entries(profiles).forEach(([userId, profile]) => {
      this.set(userId, profile);
    });
  }

  /**
   * Check if a profile is cached and not expired
   */
  has(userId: string): boolean {
    const cached = this.cache.get(userId);
    if (!cached) return false;
    
    if (Date.now() > cached.expiresAt) {
      this.cache.delete(userId);
      return false;
    }
    
    return true;
  }

  /**
   * Clear expired entries
   */
  clearExpired(): void {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now > value.expiresAt) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Start automatic cleanup of expired entries
   */
  startAutoCleanup(): void {
    // Don't start if already running
    if (this.cleanupIntervalId) return;

    this.cleanupIntervalId = setInterval(() => {
      this.clearExpired();
    }, this.CLEANUP_INTERVAL);
  }

  /**
   * Stop automatic cleanup
   */
  stopAutoCleanup(): void {
    if (this.cleanupIntervalId) {
      clearInterval(this.cleanupIntervalId);
      this.cleanupIntervalId = null;
    }
  }

  /**
   * Clear cache and stop cleanup (for logout/shutdown)
   */
  destroy(): void {
    this.stopAutoCleanup();
    this.clear();
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; hits: number; misses: number } {
    return {
      size: this.cache.size,
      hits: 0, // Would need to track this
      misses: 0 // Would need to track this
    };
  }

  private findOldestEntry(): string | null {
    let oldestKey: string | null = null;
    let oldestTime = Infinity;

    for (const [key, value] of this.cache.entries()) {
      if (value.timestamp < oldestTime) {
        oldestTime = value.timestamp;
        oldestKey = key;
      }
    }

    return oldestKey;
  }
}

// Singleton instance
export const profileCache = new ProfileCacheService();

// Note: Call profileCache.startAutoCleanup() when the app starts
// and profileCache.destroy() on logout to properly manage the cleanup interval